import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '../../Layout/Layout';
import styles from './SMSMarketing.module.css';
import { URL as BASEURL } from '../../AxiosUrlGlobal';
import { AiOutlinePlus, AiOutlineEdit, AiOutlineDelete, AiOutlineSend, AiOutlineEye } from 'react-icons/ai';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import CreateCampaignModal from './CreateCampaignModal';
import ViewCampaignModal from './ViewCampaignModal';
import EditCampaignModal from './EditCampaignModal';
import ConfirmModal from './ConfirmModal';

const SMSMarketing = () => {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [confirmConfig, setConfirmConfig] = useState({});
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCampaigns, setTotalCampaigns] = useState(0);
  const [limit, setLimit] = useState(10);

  const [selectedStatus, setSelectedStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const userId = localStorage.getItem('id');
  const token = localStorage.getItem('token');

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, selectedStatus, limit]);

  // Fetch campaigns
  useEffect(() => {
    fetchCampaigns();
  }, [currentPage, debouncedSearch, selectedStatus, limit]);

  const fetchCampaigns = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${BASEURL}superadmin/sms-marketing/${userId}`,
        {
          params: {
            page: currentPage,
            limit,
            statusFilter: selectedStatus,
            search: debouncedSearch
          },
          headers: { token }
        }
      );

      if (response.data.success) {
        setCampaigns(response.data.data.campaigns);
        setStats(response.data.data.stats);
        setTotalPages(response.data.data.pagination.totalPages);
        setTotalCampaigns(response.data.data.pagination.totalCampaigns);
        setError(null);
      } else {
        setError(response.data.error || 'Failed to fetch campaigns');
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to fetch campaigns');
    } finally {
      setLoading(false);
    }
  };

  const handleView = (campaign) => {
    setSelectedCampaign(campaign);
    setShowViewModal(true);
  };

  const handleEdit = (campaign) => {
    setSelectedCampaign(campaign);
    setShowEditModal(true);
  };

  const handleDeleteClick = (campaignId) => {
    setConfirmConfig({
      title: 'Delete Campaign',
      message: 'Are you sure you want to delete this campaign?',
      confirmText: 'Delete',
      cancelText: 'Cancel',
      type: 'danger'
    });
    setConfirmAction(() => () => handleDelete(campaignId));
    setShowConfirmModal(true);
  };

  const handleDelete = async (campaignId) => {
    try {
      const response = await axios.delete(
        `${BASEURL}superadmin/sms-marketing/${userId}/${campaignId}`,
        { headers: { token } }
      );

      if (response.data.success) {
        toast.success('Campaign deleted successfully');
        fetchCampaigns();
      } else {
        toast.error(response.data.error || 'Failed to delete campaign');
      }
    } catch (err) {
      toast.error(err.response?.data?.error || err.message || 'Failed to delete campaign');
    }
  };

  const handleSendClick = (campaignId) => {
    setConfirmConfig({
      title: 'Send Campaign',
      message: 'Are you sure you want to send this campaign? This will send SMS to all targeted recipients.',
      confirmText: 'Send',
      cancelText: 'Cancel',
      type: 'success'
    });
    setConfirmAction(() => () => handleSend(campaignId));
    setShowConfirmModal(true);
  };

  const handleSend = async (campaignId) => {
    try {
      const response = await axios.post(
        `${BASEURL}superadmin/sms-marketing/send/${userId}/${campaignId}`,
        {},
        { headers: { token } }
      );

      if (response.data.success) {
        toast.success(`Campaign sent successfully! Sent: ${response.data.data.sentCount}, Failed: ${response.data.data.failedCount}, Credits Used: ${response.data.data.creditsUsed}`);
        fetchCampaigns();
      } else {
        toast.error(response.data.error || 'Failed to send campaign');
      }
    } catch (err) {
      toast.error(err.response?.data?.error || err.message || 'Failed to send campaign');
    }
  };

  const getStatusBadge = (status, statusLabel) => {
    const statusColors = {
      0: 'gray',    // Draft
      1: 'blue',    // Scheduled
      2: 'orange',  // Sending
      3: 'green',   // Sent
      4: 'red',     // Failed
      5: 'gray'     // Cancelled
    };

    return (
      <span className={`${styles.badge} ${styles[`badge${statusColors[status]}`]}`}>
        {statusLabel}
      </span>
    );
  };

  return (
    <Layout>
      <div className={styles.container}>
        <div className={styles.header}>
          <h1 className={styles.title}>SMS Marketing</h1>
          <button className={styles.createButton} onClick={() => setShowCreateModal(true)}>
            <AiOutlinePlus /> Create Campaign
          </button>
        </div>

        {/* Create Campaign Modal */}
        <CreateCampaignModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onSuccess={fetchCampaigns}
        />

        {/* View Campaign Modal */}
        <ViewCampaignModal
          isOpen={showViewModal}
          onClose={() => {
            setShowViewModal(false);
            setSelectedCampaign(null);
          }}
          campaign={selectedCampaign}
        />

        {/* Edit Campaign Modal */}
        <EditCampaignModal
          isOpen={showEditModal}
          onClose={() => {
            setShowEditModal(false);
            setSelectedCampaign(null);
          }}
          onSuccess={fetchCampaigns}
          campaign={selectedCampaign}
        />

        {/* Confirm Modal */}
        <ConfirmModal
          isOpen={showConfirmModal}
          onClose={() => setShowConfirmModal(false)}
          onConfirm={confirmAction}
          title={confirmConfig.title}
          message={confirmConfig.message}
          confirmText={confirmConfig.confirmText}
          cancelText={confirmConfig.cancelText}
          type={confirmConfig.type}
        />

        {/* Stats Cards */}
        {stats && (
          <>
            <div className={styles.statsGrid}>
              <div className={styles.statCard}>
                <h3>Total Campaigns</h3>
                <p className={styles.statNumber}>{stats.total}</p>
              </div>
              <div className={styles.statCard}>
                <h3>Draft</h3>
                <p className={styles.statNumber}>{stats.draft}</p>
              </div>
              <div className={styles.statCard}>
                <h3>Scheduled</h3>
                <p className={styles.statNumber}>{stats.scheduled}</p>
              </div>
              <div className={styles.statCard}>
                <h3>Sent</h3>
                <p className={styles.statNumber}>{stats.sent}</p>
              </div>
              <div className={styles.statCard}>
                <h3>Failed</h3>
                <p className={styles.statNumber}>{stats.failed}</p>
              </div>
            </div>

            {/* Click Tracking Stats */}
            {stats.clickTracking && (
              <div className={styles.statsGrid}>
                <div className={styles.statCard}>
                  <h3>Total Clicks</h3>
                  <p className={styles.statNumber}>{stats.clickTracking.totalClicks || 0}</p>
                </div>
                <div className={styles.statCard}>
                  <h3>Unique Clickers</h3>
                  <p className={styles.statNumber}>{stats.clickTracking.uniqueClickers || 0}</p>
                </div>
                <div className={styles.statCard}>
                  <h3>Average CTR</h3>
                  <p className={styles.statNumber}>{stats.clickTracking.averageCTR || 0}%</p>
                </div>
                <div className={styles.statCard}>
                  <h3>Campaigns with Links</h3>
                  <p className={styles.statNumber}>{stats.clickTracking.campaignsWithLinks || 0}</p>
                </div>
              </div>
            )}
          </>
        )}

        {/* Filters */}
        <div className={styles.filters}>
          <input
            type="text"
            placeholder="Search campaigns..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={styles.searchInput}
          />

          <select
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value)}
            className={styles.select}
          >
            <option value="all">All Status</option>
            <option value="draft">Draft</option>
            <option value="scheduled">Scheduled</option>
            <option value="sending">Sending</option>
            <option value="sent">Sent</option>
            <option value="failed">Failed</option>
            <option value="cancelled">Cancelled</option>
          </select>

          <select
            value={limit}
            onChange={(e) => setLimit(Number(e.target.value))}
            className={styles.select}
          >
            <option value="10">10 per page</option>
            <option value="25">25 per page</option>
            <option value="50">50 per page</option>
            <option value="100">100 per page</option>
          </select>
        </div>

        {/* Error Message */}
        {error && (
          <div className={styles.error}>
            {error}
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div className={styles.loading}>Loading campaigns...</div>
        )}

        {/* Campaigns Table */}
        {!loading && !error && (
          <>
            <div className={styles.tableContainer}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Title</th>
                    <th>Status</th>
                    <th>Target Audience</th>
                    <th>Recipients</th>
                    <th>Sent/Failed</th>
                    <th>Clicks</th>
                    <th>CTR</th>
                    <th>Credits Used</th>
                    <th>Created</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {campaigns.length === 0 ? (
                    <tr>
                      <td colSpan="10" className={styles.emptyState}>
                        No campaigns found. Create your first SMS campaign!
                      </td>
                    </tr>
                  ) : (
                    campaigns.map((campaign) => (
                      <tr key={campaign._id}>
                        <td>
                          <div className={styles.campaignTitle}>
                            {campaign.Title}
                            <small className={styles.campaignBody}>
                              {campaign.Body.substring(0, 50)}...
                            </small>
                          </div>
                        </td>
                        <td>{getStatusBadge(campaign.Status, campaign.statusLabel)}</td>
                        <td>
                          <span className={styles.audience}>
                            {campaign.targetAudience}
                          </span>
                        </td>
                        <td>{campaign.stats.totalRecipients || 0}</td>
                        <td>
                          {campaign.stats.sentCount || 0} / {campaign.stats.failedCount || 0}
                        </td>
                        <td>
                          <div className={styles.clickData}>
                            <div>{campaign.stats.uniqueClickers || 0} / {campaign.stats.clickCount || 0}</div>
                            <small className={styles.clickLabel}>unique / total</small>
                          </div>
                        </td>
                        <td>
                          <span className={styles.ctrValue}>
                            {campaign.stats.clickRate || 0}%
                          </span>
                        </td>
                        <td>{campaign.stats.creditsUsed || 0}</td>
                        <td>{new Date(campaign.Created_At).toLocaleDateString()}</td>
                        <td>
                          <div className={styles.actions}>
                            <button
                              className={styles.actionButton}
                              title="View Details"
                              onClick={() => handleView(campaign)}
                            >
                              <AiOutlineEye />
                            </button>
                            {campaign.Status !== 3 && campaign.Status !== 2 && (
                              <>
                                <button
                                  className={styles.actionButton}
                                  title="Edit Campaign"
                                  onClick={() => handleEdit(campaign)}
                                >
                                  <AiOutlineEdit />
                                </button>
                                <button
                                  className={`${styles.actionButton} ${styles.sendButton}`}
                                  title="Send Campaign"
                                  onClick={() => handleSendClick(campaign._id)}
                                >
                                  <AiOutlineSend />
                                </button>
                              </>
                            )}
                            {campaign.Status !== 2 && (
                              <button
                                className={`${styles.actionButton} ${styles.deleteButton}`}
                                title="Delete Campaign"
                                onClick={() => handleDeleteClick(campaign._id)}
                              >
                                <AiOutlineDelete />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className={styles.pagination}>
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className={styles.paginationButton}
                >
                  Previous
                </button>

                <span className={styles.pageInfo}>
                  Page {currentPage} of {totalPages} ({totalCampaigns} campaigns)
                </span>

                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className={styles.paginationButton}
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}

        {/* Toast Container */}
        <ToastContainer
          position="top-right"
          autoClose={3000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
        />
      </div>
    </Layout>
  );
};

export default SMSMarketing;
